/**
 * Admin Coupon Pack Pricing Hooks
 *
 * React Query hooks for the global default volume-discount tier table and
 * per-user overrides used to price self-serve Buy Coupons packs.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteUserCouponPackPricing,
  fetchCouponPackPricingDefault,
  fetchUserCouponPackPricing,
  saveCouponPackPricingDefault,
  saveUserCouponPackPricing,
} from "../utils/adminApi";

export const PACK_PRICING_QUERY_KEY_PREFIX = ["admin", "coupon-pack-pricing"];
export const PACK_PRICING_DEFAULT_QUERY_KEY = [...PACK_PRICING_QUERY_KEY_PREFIX, "default"];
export const packPricingUserQueryKey = (userId) => [...PACK_PRICING_QUERY_KEY_PREFIX, "user", { userId }];

export const useCouponPackPricingDefault = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: PACK_PRICING_DEFAULT_QUERY_KEY,
    queryFn: fetchCouponPackPricingDefault,
    enabled,
    refetchOnWindowFocus: false,
  });
};

export const useSaveCouponPackPricingDefault = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tiers, override_user_rules }) =>
      saveCouponPackPricingDefault({ tiers, override_user_rules }),
    onSuccess: async () => {
      // Per-user payloads embed default_tiers / override_user_rules, so refresh everything.
      await queryClient.invalidateQueries({ queryKey: PACK_PRICING_QUERY_KEY_PREFIX });
    },
  });
};

export const useUserCouponPackPricing = (userId, { enabled = true } = {}) => {
  return useQuery({
    queryKey: packPricingUserQueryKey(userId),
    queryFn: () => fetchUserCouponPackPricing(userId),
    enabled: enabled && !!userId,
    refetchOnWindowFocus: false,
  });
};

export const useSaveUserCouponPackPricing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, tiers }) => saveUserCouponPackPricing(userId, tiers),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: packPricingUserQueryKey(variables.userId) });
    },
  });
};

export const useDeleteUserCouponPackPricing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId }) => deleteUserCouponPackPricing(userId),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: packPricingUserQueryKey(variables.userId) });
    },
  });
};
